import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { useApprovalEvents } from "./hooks/useApprovalEvents";
import { DelayedSpinner } from "../../../../components/DelayedSpinner/DelayedSpinner";
import { TextSmRegular } from "../../../../components/Typography/TypographyV2";
import { TextArea } from "../../../../components/TextArea/TextArea";
import { InfoBlockSmall } from "../../../../components/InfoBlocks/InfoBlocks";
import { StatusBadge } from "../../../../components/Status/Status";
import type { StatusColor } from "../../../../components/Status/Status";
import type {
  ApprovalEvent,
  ApprovalEventMetadataItem,
} from "../../../../types/types.Workflow";
import { LogTimeline } from "../../../../components/Logs/LogTimeline";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding-top: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const EventsList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CommentSection = styled.div`
  padding-top: ${({ theme }) => theme.resolve("spacing_md")};
  height: fit-content;
  flex-shrink: 0;
`;

const MetadataCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};

  @media (max-width: 840px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidthItem = styled.div`
  grid-column: 1 / -1;
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
`;

const Chip = styled.span`
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
`;

interface ApprovalActivityLogProps {
  approvalId: string;
  refreshKey?: number;
}

interface CommentFormData {
  comment: string;
}

export function ApprovalActivityLog({
  approvalId,
  refreshKey,
}: ApprovalActivityLogProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const eventsEndRef = useRef<HTMLDivElement>(null);
  const { register, handleSubmit, formState, errors, reset, watch, setValue } =
    useForm<CommentFormData>();
  const commentValue = watch("comment", "");

  const { events, isLoading, postComment, mutate } = useApprovalEvents({
    approvalId,
  });

  useEffect(() => {
    if (refreshKey !== undefined) {
      mutate();
    }
  }, [refreshKey, mutate]);

  useEffect(() => {
    if (eventsEndRef.current) {
      eventsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [events]);

  const getEventDescription = (event: ApprovalEvent) => {
    return event.event_title || "";
  };

  const getStatusColor = (status: string): StatusColor => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes("approved") || lowerStatus.includes("allowed")) {
      return "green";
    }
    if (lowerStatus.includes("rejected") || lowerStatus.includes("denied")) {
      return "red";
    }
    if (
      lowerStatus.includes("progress") ||
      lowerStatus.includes("pending") ||
      lowerStatus.includes("awaiting")
    ) {
      return "yellow";
    }
    return "gray";
  };

  const renderMetadataItem = (item: ApprovalEventMetadataItem) => {
    const { label, value, kind } = item;

    if (kind === "status" && value) {
      return (
        <InfoBlockSmall
          key={label}
          header={t(label)}
          content={
            <StatusBadge color={getStatusColor(value)} text={t(value)} />
          }
        />
      );
    }

    if (kind === "list" && value) {
      const items = Array.isArray(value) ? value : [value];
      return (
        <InfoBlockSmall
          key={label}
          header={t(label)}
          content={
            <ChipContainer>
              {items.map((item, idx) => (
                <Chip key={idx}>{item}</Chip>
              ))}
            </ChipContainer>
          }
        />
      );
    }

    // Default: text
    return (
      <InfoBlockSmall key={label} header={t(label)} content={value || ""} />
    );
  };

  const onSubmit = async (data: CommentFormData) => {
    if (!data.comment.trim()) return;

    setIsSubmitting(true);
    try {
      await postComment({
        event_type: "comment",
        comment: data.comment.trim(),
      });
      setValue("comment", "");
      reset();
    } catch (err) {
      // Error handled in hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  if (isLoading) {
    return <DelayedSpinner />;
  }

  return (
    <Container>
      <EventsList>
        {events.map((event) => {
          const userName = event.user
            ? `${event.user.firstname} ${event.user.lastname}`
            : t("System (Auto-Approval)");
          const eventDate = event.created_at || new Date().toISOString();
          const hasMetadata = !!(
            event.event_metadata && event.event_metadata.length
          );

          return (
            <LogTimeline
              key={event.event_id}
              id={String(event.event_id)}
              username={userName}
              date={eventDate}
              message={<span>{getEventDescription(event)}</span>}
              isExpanded={hasMetadata}
              showToggleButton={false}
            >
              {hasMetadata && (
                <MetadataCard>
                  {(event.event_metadata || []).map((item, idx) => {
                    const isFullWidth = item.label
                      .toLowerCase()
                      .includes("approver");
                    return isFullWidth ? (
                      <FullWidthItem key={`${event.event_id}-${idx}`}>
                        {renderMetadataItem(item)}
                      </FullWidthItem>
                    ) : (
                      <div key={`${event.event_id}-${idx}`}>
                        {renderMetadataItem(item)}
                      </div>
                    );
                  })}
                </MetadataCard>
              )}
            </LogTimeline>
          );
        })}

        {events.length === 0 && (
          <TextSmRegular>{t("No activity yet")}</TextSmRegular>
        )}
        <div ref={eventsEndRef} />
      </EventsList>

      <CommentSection>
        <TextArea
          name="comment"
          label={t("Add a comment...")}
          theref={register}
          formState={formState}
          required={false}
          errors={errors}
          autoHeight
          onSend={handleSubmit(onSubmit)}
          sendDisabled={!commentValue?.trim() || isSubmitting}
          sendLoading={isSubmitting}
          onKeyDown={handleKeyDown}
        />
      </CommentSection>
    </Container>
  );
}
